import React from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import "./Users.css";
import "../../App.css";
import MultiSelector from "../../components/MultiSelector";
import { roles } from "../../services/constants";
import SearchInput from "../../components/SearchInput";
import PaginatedTable from "../../components/Table/PaginatedTable";
import { RowData } from "../../Types";
import { MainContext } from "../../contexts/MainContext";
import { apiRequest } from "../../functions/requestMulti";
import {
  ListCacheEntry,
  restoreListState,
  restoreScrollPosition,
  saveListState,
} from "../../functions/listStateCache";
import debounce from "lodash.debounce";
import TextCheckbox from "../../components/TextCheckbox";

type Props = {};
type ComposedProps = Props &
  RouteComponentProps<{
    clubId: string;
    branchId: string;
  }>;

export default class Users extends React.Component<
  ComposedProps,
  {
    clubId: string;
    branchId: string;
    userSearchVal: string;
    userTableArr: Array<RowData>;
    redirect: string | null;
    // Pagination and sorting state
    page: number;
    pageSize: number;
    totalUsers: number;
    sortBy: string;
    sortOrder: "asc" | "desc";
    usersLoaded: boolean;
    showBookings: boolean;
  }
> {
  static contextType = MainContext;

  private cacheKey: string;
  private restored: ListCacheEntry | null;
  private leavingToDetail = false;
  private cancelScroll: (() => void) | null = null;
  private mounted = false;
  private rootRef = React.createRef<HTMLDivElement>();

  constructor(props: ComposedProps) {
    super(props);
    this.cacheKey = props.match.url;
    this.restored = restoreListState(this.cacheKey);
    this.state = {
      clubId: this.props.match.params.clubId,
      branchId: this.props.match.params.branchId,
      userSearchVal: "",
      userTableArr: [],
      // Pagination and sorting initialization
      page: 1,
      pageSize: 10,
      totalUsers: 0,
      sortBy: "lName",
      sortOrder: "asc",
      usersLoaded: false,
      showBookings: false,
      // Returning from a user's detail page: render the previous page, filters
      // and rows straight away instead of resetting to page 1 with skeletons.
      ...(this.restored ? this.restored.state : {}),
      redirect: null,
    };
  }

  componentDidMount = () => {
    this.mounted = true;
    if (this.restored) {
      this.cancelScroll = restoreScrollPosition(
        this.restored,
        this.rootRef.current
      );
    }
    // Also runs when restoring: requestUsers never flips usersLoaded back to
    // false, so this is a silent refresh that swaps the rows in on arrival.
    this.requestUsers();
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (this.cancelScroll) this.cancelScroll();
    this.debounceRequest.cancel();
    if (this.leavingToDetail) {
      saveListState(
        this.cacheKey,
        {
          userSearchVal: this.state.userSearchVal,
          userTableArr: this.state.userTableArr,
          page: this.state.page,
          pageSize: this.state.pageSize,
          totalUsers: this.state.totalUsers,
          sortBy: this.state.sortBy,
          sortOrder: this.state.sortOrder,
          showBookings: this.state.showBookings,
          usersLoaded: true,
        },
        this.rootRef.current
      );
    }
  };

  requestUsers = async () => {
    await apiRequest("api/branch/users", "GET", {
      targetUserId: this.context.selectedUserId,
      clubId: this.state.clubId,
      branchId: this.state.branchId,
      query: this.state.userSearchVal,
      page: this.state.page,
      hitsPerPage: this.state.pageSize,
      sortBy: this.state.sortBy,
      sortOrder: this.state.sortOrder,
      includeBookings: this.state.showBookings,
    })
      .then(this.handleUsers)
      .catch(this.handleError);
  };

  handleUsers = ({ data }: any) => {
    // apiRequest retries for a few seconds, so a response can land after the
    // screen is gone.
    if (!this.mounted) return;
    this.setState({ usersLoaded: true });
    if (data.success) {
      // Updated to match the new API response structure
      const responseData = data.data;
      const userData = responseData.users;
      const totalCount = responseData.totalCount;

      const branchRoles = this.context.branch.roles || {};

      const userTableArr = userData.map((user: any) => {
        const userRoles = user.roles
          .filter((roleId: string) => branchRoles[roleId])
          .map((roleId: string) => {
            return {
              id: roleId,
              name: branchRoles[roleId].name,
              selected: true,
              color:
                branchRoles[roleId].type === "club" ? "#5c67ee" : "#ee5c5c",
              notEditable: branchRoles[roleId].type === "club",
            };
          })
          .sort((a: any, b: any) => (a.name < b.name ? 1 : -1));

        const bookingsTable =
          user.bookings?.map((booking: { id: string; title: string }) => ({
            id: booking.id,
            name: booking.title,
            selected: true,
            color: "#5c67ee",
          })) || [];

        return {
          cells: [
            {
              value: user.membershipNumber,
              sort: user.membershipNumber,
              search: user.membershipNumber,
            },
            {
              value: user.fName,
              sort: user.fName,
              search: `${user.fName} ${user.lName}`,
            },
            {
              value: user.lName,
              sort: user.lName,
              search: user.lName,
            },
            {
              value: user.email,
              sort: user.email,
              search: user.email,
            },
            {
              value: roles[user.role]?.germName || user.role,
              sort: roles[user.role]?.germName || user.role,
              search: roles[user.role]?.germName || user.role,
            },
            {
              value: (
                <MultiSelector
                  viewOnly
                  onSelect={() => {}}
                  onUnselect={() => {}}
                  arr={this.state.showBookings ? bookingsTable : userRoles}
                />
              ),
              sort: "",
              search: [
                ...(user.roleArr || []).map((role: any) => role.name),
                ...(user.courseArr || []).map((course: any) => course.name),
              ].join(" "),
            },
          ],
          onClick: `/club/${this.state.clubId}/branch-settings/${this.state.branchId}/users/${user.id}`,
        };
      });

      this.setState({
        userTableArr,
        totalUsers: totalCount,
      });
    }
  };

  handleError = (err: any) => {
    console.error(err);
  };

  handleChangeClubSearchVal = (val: any) => {
    this.setState({ userSearchVal: val, page: 1 }, this.debounceRequest);
  };

  debounceRequest = debounce(() => {
    this.requestUsers();
  }, 500);

  handlePageChange = (newPage: number) => {
    this.setState({ page: newPage }, this.requestUsers);
  };

  handlePageSizeChange = (newPageSize: number) => {
    this.setState({ pageSize: newPageSize, page: 1 }, this.requestUsers);
  };

  handleSortChange = (sortKey: string) => {
    const newSortOrder =
      this.state.sortBy === sortKey && this.state.sortOrder === "asc"
        ? "desc"
        : "asc";

    this.setState(
      {
        sortBy: sortKey,
        sortOrder: newSortOrder,
        page: 1, // Reset to first page when sorting changes
      },
      this.requestUsers
    );
  };

  handleRedirect = (redirect: string) => {
    // Only a row click leaves a snapshot behind - navigating away via the
    // sidebar must still give a fresh list on the way back.
    this.leavingToDetail = true;
    this.props.history.push({
      pathname: redirect,
    });
  };

  toggleShowAllCourseBookings = () => {
    this.setState(
      (prevState) => ({
        showBookings: !prevState.showBookings,
      }),
      this.requestUsers
    );
  };

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }
    return (
      <>
        <div className="subscreen-main-container" ref={this.rootRef}>
          <div className="subscreen-box-container">
            <SearchInput
              name="course-search-input"
              placeholder="Nutzersuche"
              value={this.state.userSearchVal}
              onChange={this.handleChangeClubSearchVal}
              className="no-margin"
            />
            <TextCheckbox
              text="Buchungen anzeigen"
              val={this.state.showBookings}
              onChange={this.toggleShowAllCourseBookings}
            />
            <div className="medium-space"></div>
            <PaginatedTable
              header={[
                {
                  title: "Mitgliedsnummer",
                  displayWidth: 450,
                  sort: "membershipNumber",
                },
                { title: "Vorname", sort: "fName" },
                { title: "Nachname", sort: "lName" },
                { title: "E-Mail", displayWidth: 950, sort: "email" },
                { title: "Rechte", displayWidth: 600 },
                {
                  title: this.state.showBookings ? "Kursbuchungen" : "Rollen",
                  displayWidth: 1200,
                },
              ]}
              data={this.state.userTableArr}
              loaded={this.state.usersLoaded}
              skeletonRows={10}
              onClick={this.handleRedirect}
              defaultSortDirection={this.state.sortOrder}
              defaultSortKey={this.state.sortBy}
              page={this.state.page}
              pageSize={this.state.pageSize}
              entryCount={this.state.totalUsers}
              onPageChange={this.handlePageChange}
              onPageSizeChange={this.handlePageSizeChange}
              onSortChange={this.handleSortChange}
            />
          </div>
        </div>
      </>
    );
  }
}
